import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

// Portal Work (v5) — custom sort dropdown for the module header, replacing the
// native <select>. The native control was accessible for free; this rebuild
// re-earns that bar deliberately with the WAI-ARIA collapsible listbox pattern:
//   • trigger button: aria-haspopup="listbox" + aria-expanded
//   • popup: role="listbox" that RECEIVES focus on open, with role="option"
//     children tracked by aria-activedescendant (no roving tabindex)
//   • keyboard: ↑/↓ move the active option, Home/End jump, Enter/Space select,
//     Esc/Tab close — every close path returns focus to the trigger
//   • click-outside closes; selecting closes and returns focus
// Behaviour + option set are unchanged from the shipped control — this is UI only.

function SortMenu({ value, options, onChange }) {
	const [open, setOpen] = useState(false);
	// Active (highlighted) option while navigating — seeded from the selection.
	const [activeKey, setActiveKey] = useState(value);
	const triggerRef = useRef(null);
	const listRef = useRef(null);
	const baseId = useId();

	const current = options.find((o) => o.key === value) || options[0];
	const optionId = (key) => `${baseId}-opt-${key}`;

	const close = useCallback((returnFocus = true) => {
		setOpen(false);
		if (returnFocus && triggerRef.current) triggerRef.current.focus();
	}, []);

	const commit = useCallback(
		(key) => {
			if (key !== value) onChange(key);
			close();
		},
		[value, onChange, close]
	);

	// On open: focus the listbox and highlight the current selection so the arrow
	// keys start from where the user already is.
	useEffect(() => {
		if (!open) return undefined;
		setActiveKey(value);
		const raf = requestAnimationFrame(() => {
			if (listRef.current) listRef.current.focus();
		});
		return () => cancelAnimationFrame(raf);
	}, [open, value]);

	// Close on any pointer press outside the trigger + popup. Pointerdown (not
	// click) so it fires before focus churn.
	useEffect(() => {
		if (!open) return undefined;
		const onDocDown = (e) => {
			if (
				triggerRef.current?.contains(e.target) ||
				listRef.current?.contains(e.target)
			)
				return;
			setOpen(false);
		};
		document.addEventListener('pointerdown', onDocDown);
		return () => document.removeEventListener('pointerdown', onDocDown);
	}, [open]);

	const moveActive = (delta) => {
		const i = options.findIndex((o) => o.key === activeKey);
		const from = i < 0 ? options.findIndex((o) => o.key === value) : i;
		const next = Math.min(options.length - 1, Math.max(0, from + delta));
		setActiveKey(options[next].key);
	};

	const onListKeyDown = (e) => {
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				moveActive(1);
				break;
			case 'ArrowUp':
				e.preventDefault();
				moveActive(-1);
				break;
			case 'Home':
				e.preventDefault();
				setActiveKey(options[0].key);
				break;
			case 'End':
				e.preventDefault();
				setActiveKey(options[options.length - 1].key);
				break;
			case 'Enter':
			case ' ':
			case 'Spacebar':
				e.preventDefault();
				commit(activeKey);
				break;
			case 'Escape':
				e.preventDefault();
				close();
				break;
			case 'Tab':
				// Let focus leave naturally, but drop the popup.
				setOpen(false);
				break;
			default:
				break;
		}
	};

	const onTriggerKeyDown = (e) => {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setOpen(true);
		}
	};

	return (
		<div className="wt-sortmenu">
			<button
				ref={triggerRef}
				type="button"
				className="wt-sortmenu__trigger"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={`Sort tasks — ${current.label}`}
				onClick={() => setOpen((v) => !v)}
				onKeyDown={onTriggerKeyDown}>
				<i className="fa-sharp fa-solid fa-arrow-down-wide-short wt-sortmenu__icon" />
				<span className="wt-sortmenu__label">{current.label}</span>
				<i className="fa-sharp fa-solid fa-chevron-down wt-sortmenu__chev" />
			</button>
			{open && (
				<ul
					ref={listRef}
					className="wt-sortmenu__list"
					role="listbox"
					tabIndex={-1}
					aria-label="Sort tasks"
					aria-activedescendant={optionId(activeKey)}
					onKeyDown={onListKeyDown}>
					{options.map((o) => {
						const selected = o.key === value;
						const active = o.key === activeKey;
						return (
							<li
								key={o.key}
								id={optionId(o.key)}
								role="option"
								aria-selected={selected}
								className={`wt-sortmenu__opt${active ? ' is-active' : ''}${
									selected ? ' is-selected' : ''
								}`}
								onMouseEnter={() => setActiveKey(o.key)}
								onClick={() => commit(o.key)}>
								<span className="wt-sortmenu__optcheck">
									{selected && <i className="fa-sharp fa-solid fa-check" />}
								</span>
								{o.label}
							</li>
						);
					})}
					<li className="wt-sortmenu__note" role="presentation">
						Tasks without a value sort last
					</li>
				</ul>
			)}
		</div>
	);
}

export default SortMenu;
